import React, { useEffect, useMemo, useState } from 'react';
import { CheckCheck, ClipboardPaste, Eraser, Plus, Save, Settings2, Trash2 } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import Modal from '../../components/Modal';
import { DateInput, Select } from '../../components/Controls';
import {
  dmy, ErrorBox, exportRows, letters, Loading, matches, n2, Panel, PeriodPick, plabel, PyHeader, SearchBox, Sel, SRC_LABEL, Stat, Toolbar,
  usePayrollMonth, Workbook,
} from './shared';

// Attendance Management > Attendance Import and Attendance Manual (Stephanet's pay15/Attendance: PyAttImport, PyAttManual).
// Import: paste any attendance report with its heading row; columns are found from their headings (or chosen by hand),
// people by employee number, Device PIN or name, sites by name or alias; Save keeps the paste, Apply replaces the month's
// imported lines. Manual: typed lines that count over the device line for that person and day.

const FIELDS = ['id', 'name', 'date', 'site', 'in', 'out', 'break', 'hours', 'ot', 'status'];

function parseTsv(text) {
  const lns = text.replace(/\r/g, '').split('\n').filter(l => l.trim() !== '');
  if (!lns.length) return { headers: [], rows: [] };
  const split = l => (l.includes('\t') ? l.split('\t') : l.split(','));
  return { headers: split(lns[0]).map(x => x.trim()), rows: lns.slice(1).map(l => split(l).map(x => x.trim())) };
}

export function AttendanceImport() {
  const { notify, canManage, confirm } = useHR();
  const [cal, period, setPeriod] = usePayrollMonth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [paste, setPaste] = useState(null);   // {headers, rows} pasted but not saved yet
  const [assigned, setAssigned] = useState({});
  const [show, setShow] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [setts, setSetts] = useState(null);
  const load = () => hr.getAttendanceImport(period).then(x => { setD(x); setAssigned(x.assigned || {}); setPaste(null); setErr(''); })
    .catch(e => setErr(e.message));
  useEffect(() => { if (period) load(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps
  const lines = useMemo(() => (d ? d.lines.filter(l => (!show || (show === 'bad' ? l.problem : show === 'ok' ? l.check.startsWith('OK') : !l.used))
    && matches(l, q, ['employee_no', 'name', 'site', 'site_raw', 'check'])) : []), [d, show, q]);
  if (err) return <ErrorBox error={err} />;
  if (!d || !cal) return <Loading what="the attendance import" />;
  const edit = canManage;
  const body = preview => ({ ...(paste || { headers: d.headers, rows: d.lines.map(l => l.values) }), assigned, preview });
  const save = async preview => {
    setBusy(true);
    try {
      const r = await hr.saveAttendanceImport(period, body(preview));
      if (preview) setD(x => ({ ...x, ...r, assigned: x.assigned })); else { notify(r.message); load(); }
    } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try { const r = await hr.applyAttendanceImport(period); notify(r.message); load(); } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const onPaste = text => {
    const p = parseTsv(text);
    if (!p.headers.length) return;
    setPaste(p);
    hr.saveAttendanceImport(period, { ...p, assigned, preview: true }).then(r => setD(x => ({ ...x, ...r, assigned: x.assigned })))
      .catch(e => notify(e.message, 'bad'));
  };
  const clear = async () => {
    let all = false;
    const ok = await confirm({ title: 'Clear the paste area', danger: true, confirmLabel: 'Clear',
      message: 'Clear the pasted report for this month?',
      detail: <label className="rec-check py-mt"><input type="checkbox" onChange={e => { all = e.target.checked; }} /> Also remove the lines already applied to attendance</label> });
    if (!ok) return;
    try { const r = await hr.clearAttendanceImport(period, all); notify(r.message); load(); } catch (e) { notify(e.message, 'bad'); }
  };
  const cols = [{ key: 'row', label: 'Row', kind: 'num', w: 50 }, { key: 'employee_no', label: 'Payroll ID', w: 90 }, { key: 'name', label: 'Name', w: 160 },
    { key: 'date', label: 'Work date', w: 100, get: l => (l.date ? dmy(l.date) : '') }, { key: 'site', label: 'Payroll project', w: 170, get: l => l.site || l.site_raw || '' },
    { key: 'time_in', label: 'Time in', w: 70 }, { key: 'time_out', label: 'Time out', w: 70 },
    { key: 'break_minutes', label: 'Break (mins)', kind: 'num', w: 70 },
    { key: 'hours_on_site', label: 'Hours on site', kind: 'num', sum: true, w: 80 }, { key: 'ot_shown', label: 'OT on report (not paid)', kind: 'num', w: 90 },
    { key: 'shift', label: 'Work shift', w: 90 }, { key: 'hours', label: 'Normal hours (within shift) → site sheet', kind: 'num', sum: true, w: 110 },
    { key: 'tp', label: 'TP', kind: 'num', sum: true, w: 50, get: l => (l.tp ? 1 : 0) },
    { key: 'check', label: 'Check', w: 330, cls: 'py-chk' }];
  const st = d.settings;
  const changed = FIELDS.some(k => (assigned[k] ?? null) !== ((d.assigned || {})[k] ?? null));
  return (
    <div className="ps py">
      <PyHeader title="Attendance Import" sub="Paste any attendance report including its heading row — the columns are found by their headings and the site sheets fill themselves." cal={cal} period={period}>
        <Toolbar onExport={() => exportRows(`attendance-import-${period}.csv`, cols, d.lines)}>
          {edit && <button className="mdr-btn" onClick={() => setSetts({ missing_out_hours: st.missing_out_hours, full_day_hours: st.full_day_hours, headings: JSON.parse(JSON.stringify(st.headings)) })}><Settings2 size={14} /> Settings & Headings</button>}
          {edit && <button className="mdr-btn" onClick={clear}><Eraser size={14} /> Clear</button>}
          {edit && <button className="mdr-btn" disabled={busy || !(paste || d.pasted)} onClick={() => save(false)}><Save size={14} /> Save Paste</button>}
          {edit && <button className="mds-run" disabled={busy || !!paste || !d.pasted} onClick={apply} title={paste ? 'Save the paste first' : ''}><CheckCheck size={14} /> Apply to Attendance</button>}
        </Toolbar>
      </PyHeader>
      <div className="mdr-card ps-filters no-print"><PeriodPick cal={cal} value={period} onChange={setPeriod} /><div className="ps-runbar inline">Period {d.period.text}</div></div>
      <div className="py-stats">
        <Stat label="Rows pasted" value={d.pasted} /><Stat label="Used this pay period" value={d.used} tone="green" />
        <Stat label="Need attention" value={d.attention} tone={d.attention ? 'red' : ''} /><Stat label="Lines applied to attendance" value={d.applied} tone="blue" />
        <Stat label="No time out — count" value={`${st.missing_out_hours} h`} /><Stat label="Full day (8 h) when on site ≥" value={`${st.full_day_hours} h`} />
        <Stat label="Overtime from device" value="NONE — enter by hand" />
      </div>
      {paste && <div className="py-banner">New paste: {paste.rows.length} line(s) — previewed below. Click <b>Save Paste</b>, then <b>Apply to Attendance</b>.</div>}
      {d.saved_at && !paste && <div className="muted small py-foot">Saved {dmy(d.saved_at)}{d.saved_by ? ` by ${d.saved_by}` : ''}{d.applied_at ? ` · applied ${dmy(d.applied_at)}${d.applied_by ? ` by ${d.applied_by}` : ''}` : ' · not applied yet'}</div>}
      <div className="hre-grid2">
        <Panel title="Paste Area" note="① clear last month ② copy your report with its heading row ③ paste here">
          <textarea className="py-paste" disabled={!edit} placeholder={'Click here and paste (Ctrl+V) — e.g.\nStaff ID\tStaff Name\tSite\tDate\tClock In\tClock Out\tBreak (mins)\tOvertime (hrs)\tStatus'}
                    value="" onChange={() => {}} onPaste={e => { e.preventDefault(); onPaste(e.clipboardData.getData('text')); }} />
          <p className="muted small"><ClipboardPaste size={12} /> Copy straight from Excel / the biometric export (tab-separated) or a CSV. Dates as 21/02/2026, 2026-02-21 or Excel dates.</p>
        </Panel>
        <Panel title="Columns Found" note={`${(paste || d).headers.length} heading(s) in the paste`}>
          <table className="ps-table py-simple">
            <thead><tr><th>Field</th><th>Found in column</th><th>Or choose the column</th></tr></thead>
            <tbody>{FIELDS.map(fl => {
              const c = d.columns[fl];
              const need = ['date', 'site'].includes(fl);
              const headers = (paste || d).headers;
              return (
                <tr key={fl}><td>{st.labels[fl]}</td>
                  <td className={c === null && need ? 'py-bad' : ''}>{c !== null && c !== undefined ? `${letters(c)} — ${headers[c] || ''}` : need ? 'MISSING' : '—'}</td>
                  <td><Select className="py-cell" disabled={!edit} value={assigned[fl] ?? ''} title="Column"
                              onChange={e => setAssigned({ ...assigned, [fl]: e.target.value === '' ? null : Number(e.target.value) })}>
                    <option value="">(auto)</option>{headers.map((h, i) => <option key={i} value={i}>{letters(i)} — {h}</option>)}</Select></td></tr>
              );
            })}</tbody>
          </table>
          {edit && changed && <button className="mdr-btn py-add" onClick={() => save(true)}>Re-check with these columns</button>}
        </Panel>
      </div>
      <div className="mdr-card ps-filters no-print">
        <Sel label="Show" value={show} onChange={setShow} all="All lines" options={[{ value: 'bad', label: 'Need attention' }, { value: 'ok', label: 'OK' }, { value: 'ignored', label: 'Not used' }]} />
        <SearchBox value={q} onChange={setQ} placeholder="Search name, ID, site, check…" />
      </div>
      <Workbook cols={cols} rows={lines} frozen={3} maxHeight={520} rowClass={l => (l.problem ? 'py-row-bad' : !l.used ? 'py-row-dim' : '')} empty="Nothing pasted for this month." />
      {setts && <ImportSettings value={setts} labels={st.labels} onClose={() => setSetts(null)}
        onSave={async v => { try { await hr.saveAttendanceImportSettings(v); notify('Attendance import settings saved'); setSetts(null); load(); } catch (e) { notify(e.message, 'bad'); } }} />}
    </div>
  );
}

function ImportSettings({ value, labels, onClose, onSave }) {
  const [s, setS] = useState(value);
  return (
    <Modal title="Attendance Import Settings" wide onClose={onClose}>
      <div className="rec-form-grid">
        <label className="rec-f"><span>No time out: count this many hours</span><input type="number" value={s.missing_out_hours} onChange={e => setS({ ...s, missing_out_hours: Number(e.target.value) })} /></label>
        <label className="rec-f"><span>Full day (8 h) when hours on site are at least</span><input type="number" value={s.full_day_hours} onChange={e => setS({ ...s, full_day_hours: Number(e.target.value) })} /></label>
        <p className="muted small">Extra hours on the device are never overtime. Overtime requested by management is entered in Mark Daily Attendance or Attendance Manual.</p>
      </div>
      <h4 className="wh-sub">Headings the import recognises — add your own report's heading names</h4>
      <div className="py-headings">{FIELDS.map(fl => (
        <label key={fl} className="rec-f"><span>{labels[fl]}</span>
          <input value={(s.headings[fl] || []).join(', ')} onChange={e => setS({ ...s, headings: { ...s.headings, [fl]: e.target.value.split(',').map(x => x.trim()).filter(Boolean) } })} /></label>
      ))}</div>
      <div className="mdh-form-foot"><button className="mdr-btn" onClick={onClose}>Cancel</button><button className="mds-run" onClick={() => onSave(s)}>Save Settings</button></div>
    </Modal>
  );
}

// ── Attendance Manual ──
export function AttendanceManual() {
  const { notify, canManage, confirm } = useHR();
  const [cal, period, setPeriod] = usePayrollMonth();
  const [d, setD] = useState(null);
  const [emps, setEmps] = useState([]);
  const [sites, setSites] = useState([]);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  const load = () => hr.getAttendanceManual(period).then(x => { setD(x); setErr(''); }).catch(e => setErr(e.message));
  useEffect(() => { if (period) load(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    hr.listEmployees().then(x => setEmps(x.filter(e => ['active', 'probation'].includes(e.employment_status)))).catch(() => {});
    hr.listSites().then(setSites).catch(() => {});
  }, []);
  const rows = useMemo(() => (d ? d.rows.filter(r => matches(r, q, ['employee_no', 'name', 'site', 'reason', 'check'])) : []), [d, q]);
  if (err) return <ErrorBox error={err} />;
  if (!d || !cal) return <Loading what="manual attendance" />;
  const cols = [{ key: 'employee_no', label: 'Employee ID', w: 96 }, { key: 'name', label: 'Name', w: 170 }, { key: 'date', label: 'Date', w: 100, get: r => dmy(r.date) },
    { key: 'site', label: 'Project / site', w: 190 }, { key: 'hours', label: 'Normal hours (8 = full day)', kind: 'num', sum: true, w: 100 },
    { key: 'ot', label: 'Overtime hours', kind: 'num', sum: true, w: 90 }, { key: 'tp', label: 'TP', w: 50, get: r => (r.tp ? 1 : 0) },
    { key: 'source', label: 'Source', w: 90, get: r => `${SRC_LABEL[r.source] || r.source}${r.counts ? '' : ' (under)'}` },
    { key: 'reason', label: 'Reason / note', w: 200 }, { key: 'day', label: 'Day of pay period', kind: 'num', w: 80 }, { key: 'check', label: 'Check', w: 230 }];
  const remove = async v => {
    if (!await confirm({ title: 'Remove line', message: `Remove ${v.name}'s manual line for ${dmy(v.date)}?`, danger: true, confirmLabel: 'Remove' })) return;
    try { const r = await hr.deleteAttendanceManual(v.id); notify(r.message); setEdit(null); load(); } catch (e) { notify(e.message, 'bad'); }
  };
  return (
    <div className="ps py">
      <PyHeader title="Attendance Manual" sub="One line per person per day. A line here COUNTS OVER the device / imported line for that person and day — e.g. a forgotten clock-out, or a site without the device. Hours 0 = absent." cal={cal} period={period}>
        <Toolbar onExport={() => exportRows(`attendance-manual-${period}.csv`, cols, rows)}>
          {canManage && <button className="mds-run" onClick={() => setEdit({ hours: 8, ot: 0, tp: false })}><Plus size={14} /> Add Line</button>}
        </Toolbar>
      </PyHeader>
      <div className="mdr-card ps-filters no-print"><PeriodPick cal={cal} value={period} onChange={setPeriod} /><SearchBox value={q} onChange={setQ} />
        <div className="ps-runbar inline">Period {d.period.text} · lines needing attention: <b>{d.rows.filter(r => r.problem).length}</b></div></div>
      <Workbook cols={cols} rows={rows} frozen={2} maxHeight={600} rowClass={r => (r.problem ? 'py-row-bad' : !r.counts ? 'py-row-dim' : '')}
                onRow={canManage ? r => setEdit({ ...r, employee_id: String(r.employee_id), site_id: r.site_id ? String(r.site_id) : '' }) : undefined}
                empty={`No manual, device or leave lines for ${plabel(period)}.`} />
      {edit && <ManualForm value={edit} emps={emps} sites={sites} period={d.period} onClose={() => setEdit(null)} onRemove={edit.id && edit.source === 'manual' ? () => remove(edit) : null}
        onSave={async v => {
          const b = { employee_id: v.employee_id, date: v.date, site_id: v.site_id, hours: Number(v.hours || 0), ot: Number(v.ot || 0), tp: !!v.tp, reason: v.reason || '' };
          const r = v.id ? await hr.updateAttendanceManual(period, v.id, b) : await hr.addAttendanceManual(period, b);
          notify(r.message); setEdit(null); load();
        }} />}
      <p className="muted small py-foot">Hours worked ≈ {n2(rows.reduce((t, r) => t + (r.counts ? r.hours || 0 : 0), 0))} normal hours on {rows.filter(r => r.counts).length} counting line(s). Dimmed lines sit under a typed line and do not count.</p>
    </div>
  );
}

function ManualForm({ value, emps, sites, period, onClose, onSave, onRemove }) {
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    const miss = [['employee_id', 'Employee'], ['date', 'Date'], ['site_id', 'Project / site']].filter(([k]) => !f[k]).map(([, l]) => l);
    if (miss.length) { setErr(`Please fill: ${miss.join(', ')}`); return; }
    setBusy(true); setErr('');
    try { await onSave(f); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const typingOver = f.id && f.source && f.source !== 'manual';
  return (
    <Modal title={f.id ? `Edit line — ${f.name}` : 'Add Attendance Line'} wide onClose={onClose}>
      {typingOver && <p className="muted small py-note">This is a {SRC_LABEL[f.source] || f.source} line. Saving types the person's manual line for the day; the {SRC_LABEL[f.source] || f.source} line stays underneath.</p>}
      <div className="rec-form-grid">
        <label className="rec-f"><span>Employee *</span>
          <Select value={f.employee_id || ''} disabled={!!f.id} onChange={e => set('employee_id', e.target.value)} title="Employee">
            <option value="">—</option>{emps.map(e => <option key={e.id} value={String(e.id)}>{e.employee_no} — {e.full_name}</option>)}</Select></label>
        <label className="rec-f"><span>Date *</span><DateInput value={f.date || ''} disabled={!!f.id} onChange={e => set('date', e.target.value)} />
          <small className="muted">Within {period.text}</small></label>
        <label className="rec-f"><span>Project / site *</span>
          <Select value={f.site_id || ''} onChange={e => set('site_id', e.target.value)} title="Project / site">
            <option value="">—</option>{sites.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}</Select></label>
        <label className="rec-f"><span>Normal hours (8 = full day, 0 = absent) *</span><input type="number" min="0" max="24" step="any" value={f.hours ?? ''} onChange={e => set('hours', e.target.value)} /></label>
        <label className="rec-f"><span>Overtime hours</span><input type="number" min="0" max="24" step="any" value={f.ot ?? ''} onChange={e => set('ot', e.target.value)} /></label>
        <label className="rec-check"><input type="checkbox" checked={!!f.tp} onChange={e => set('tp', e.target.checked)} /> TP (site transport due)</label>
        <label className="rec-f s2"><span>Reason / note</span><textarea rows={3} value={f.reason || ''} onChange={e => set('reason', e.target.value)} /></label>
      </div>
      {onRemove && <button className="mdr-btn danger py-del" onClick={onRemove}><Trash2 size={13} /> Remove line</button>}
      {err && <div className="error py-err">{err}</div>}
      <div className="mdh-form-foot rec-foot">
        <button className="mdr-btn" onClick={onClose}>Cancel</button>
        <button className="mds-run" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

export default AttendanceImport;
